/* ==========================================================================
   blv-coming-soon.js — country combobox + phone composition for the gate form

   The country control is a listbox, not a <select>: each row needs a flag
   glyph, the country name and a dimmed dial code, none of which a native
   <option> can carry. The trigger stays compact (flag + code) so the row fits
   at 320px; the full name lives in the open list.

   Shopify's country object carries iso_code and name but no calling code, so
   the country LIST comes from Shopify (localization.available_countries, via
   the JSON block the section prints) and the CODES come from the table below.

   On submit the phone field's value is rewritten to its E.164 form. The field
   is named, so a raw number still posts if this script never runs.
   ========================================================================== */

(function () {
  'use strict';

  var MIN_DIGITS = 6;
  var MAX_DIGITS = 15; // E.164 caps the whole number, country code included.
  var SEARCH_AFTER = 12; // Rows before the search box earns its place.

  /* Shopify will not create a customer without an email, and this form asks
     only for a number — so one is derived from it. .invalid is the RFC 2606
     reserved TLD: it can never resolve, so these are unmistakably placeholders
     rather than addresses anyone should try to reach. */
  var EMAIL_DOMAIN = 'sms.invalid';

  /* ISO 3166-1 alpha-2 -> ITU-T E.164 calling code. */
  var DIAL = {
    AD: '376', AE: '971', AF: '93', AG: '1', AI: '1', AL: '355', AM: '374',
    AO: '244', AR: '54', AS: '1', AT: '43', AU: '61', AW: '297', AX: '358',
    AZ: '994', BA: '387', BB: '1', BD: '880', BE: '32', BF: '226', BG: '359',
    BH: '973', BI: '257', BJ: '229', BL: '590', BM: '1', BN: '673', BO: '591',
    BQ: '599', BR: '55', BS: '1', BT: '975', BW: '267', BY: '375', BZ: '501',
    CA: '1', CC: '61', CD: '243', CF: '236', CG: '242', CH: '41', CI: '225',
    CK: '682', CL: '56', CM: '237', CN: '86', CO: '57', CR: '506', CU: '53',
    CV: '238', CW: '599', CX: '61', CY: '357', CZ: '420', DE: '49', DJ: '253',
    DK: '45', DM: '1', DO: '1', DZ: '213', EC: '593', EE: '372', EG: '20',
    ER: '291', ES: '34', ET: '251', FI: '358', FJ: '679', FK: '500', FM: '691',
    FO: '298', FR: '33', GA: '241', GB: '44', GD: '1', GE: '995', GF: '594',
    GG: '44', GH: '233', GI: '350', GL: '299', GM: '220', GN: '224', GP: '590',
    GQ: '240', GR: '30', GT: '502', GU: '1', GW: '245', GY: '592', HK: '852',
    HN: '504', HR: '385', HT: '509', HU: '36', ID: '62', IE: '353', IL: '972',
    IM: '44', IN: '91', IO: '246', IQ: '964', IR: '98', IS: '354', IT: '39',
    JE: '44', JM: '1', JO: '962', JP: '81', KE: '254', KG: '996', KH: '855',
    KI: '686', KM: '269', KN: '1', KP: '850', KR: '82', KW: '965', KY: '1',
    KZ: '7', LA: '856', LB: '961', LC: '1', LI: '423', LK: '94', LR: '231',
    LS: '266', LT: '370', LU: '352', LV: '371', LY: '218', MA: '212',
    MC: '377', MD: '373', ME: '382', MF: '590', MG: '261', MH: '692',
    MK: '389', ML: '223', MM: '95', MN: '976', MO: '853', MP: '1', MQ: '596',
    MR: '222', MS: '1', MT: '356', MU: '230', MV: '960', MW: '265', MX: '52',
    MY: '60', MZ: '258', NA: '264', NC: '687', NE: '227', NF: '672',
    NG: '234', NI: '505', NL: '31', NO: '47', NP: '977', NR: '674', NU: '683',
    NZ: '64', OM: '968', PA: '507', PE: '51', PF: '689', PG: '675', PH: '63',
    PK: '92', PL: '48', PM: '508', PR: '1', PS: '970', PT: '351', PW: '680',
    PY: '595', QA: '974', RE: '262', RO: '40', RS: '381', RU: '7', RW: '250',
    SA: '966', SB: '677', SC: '248', SD: '249', SE: '46', SG: '65', SH: '290',
    SI: '386', SJ: '47', SK: '421', SL: '232', SM: '378', SN: '221', SO: '252',
    SR: '597', SS: '211', ST: '239', SV: '503', SX: '1', SY: '963', SZ: '268',
    TC: '1', TD: '235', TG: '228', TH: '66', TJ: '992', TK: '690', TL: '670',
    TM: '993', TN: '216', TO: '676', TR: '90', TT: '1', TV: '688', TW: '886',
    TZ: '255', UA: '380', UG: '256', US: '1', UY: '598', UZ: '998', VA: '39',
    VC: '1', VE: '58', VG: '1', VI: '1', VN: '84', VU: '678', WF: '681',
    WS: '685', XK: '383', YE: '967', YT: '262', ZA: '27', ZM: '260', ZW: '263'
  };

  /* ISO letters -> regional indicator pair. Renders as a flag wherever a font
     supplies one; the section loads Noto Color Emoji ahead of Segoe UI Emoji
     so Windows has one too. */
  function flagEmoji(iso) {
    if (!String.fromCodePoint) return '';
    try {
      return iso.replace(/[A-Z]/g, function (ch) {
        return String.fromCodePoint(0x1f1e6 + ch.charCodeAt(0) - 65);
      });
    } catch (e) {
      return '';
    }
  }

  /* Country names for the every-country list, which Shopify does not supply. */
  var REGION_NAMES = (function () {
    try {
      if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
        return new Intl.DisplayNames([document.documentElement.lang || 'en'], {
          type: 'region'
        });
      }
    } catch (e) {
      /* falls through to the ISO code */
    }
    return null;
  })();

  function regionName(iso) {
    if (REGION_NAMES) {
      try {
        /* .of() throws on user-assigned codes such as XK. */
        return REGION_NAMES.of(iso) || iso;
      } catch (e) {
        return iso;
      }
    }
    return iso;
  }

  function digitsOnly(value) {
    return (value || '').replace(/\D+/g, '');
  }

  /* Numbering plans where a leading 0 is part of the national number rather
     than a trunk prefix, so it must survive. */
  var KEEP_ZERO = { IT: 1, VA: 1, CI: 1 };

  /* Strips a trunk prefix or a duplicated country code, so "+1 555…",
     "001 555…", "0555…" and "(555) …" all resolve to the same national
     number — including on a re-render, where the field already holds E.164. */
  function nationalDigits(raw, dialDigits, iso) {
    var digits = digitsOnly(raw);
    /* Only a leading + or 00 means the value already carries a country code.
       Without this guard a Danish number typed nationally as "45 12 34 56"
       would lose its first two digits to the +45 selection. */
    var intl = /^\s*\+/.test(raw || '');

    if (dialDigits && digits.indexOf('00' + dialDigits) === 0) {
      digits = digits.slice(2 + dialDigits.length);
    } else if (
      intl &&
      dialDigits &&
      digits.indexOf(dialDigits) === 0 &&
      digits.length > dialDigits.length + MIN_DIGITS - 1
    ) {
      digits = digits.slice(dialDigits.length);
    } else if (digits.charAt(0) === '0' && !KEEP_ZERO[iso]) {
      digits = digits.replace(/^0+/, '');
    }

    return digits;
  }

  /* One error box serves both fields. The offending field is flagged so the
     message is programmatically tied to it, and the other is cleared — a
     corrected email should not leave the number still marked invalid. */
  function setError(form, message, field) {
    var box = form.querySelector('[data-blv-cs-clienterror]');
    var fields = form.querySelectorAll('[data-blv-cs-tel], [data-blv-cs-emailfield]');

    if (box) {
      box.textContent = message || '';
      box.hidden = !message;
    }

    Array.prototype.forEach.call(fields, function (el) {
      if (message && el === field) {
        el.setAttribute('aria-invalid', 'true');
      } else {
        el.removeAttribute('aria-invalid');
      }
    });
  }

  /* Shopify's list when the section asked for it, every known country
     otherwise — or when the store has only one market configured, which would
     otherwise leave a one-entry list. */
  function countryList(root) {
    var list = [];

    if ((root.dataset.source || 'markets') !== 'all') {
      var node = root.querySelector('[data-blv-cs-countries]');
      if (node) {
        try {
          list = JSON.parse(node.textContent) || [];
        } catch (e) {
          list = [];
        }
      }
    }

    list = list.filter(function (c) {
      return c && c.iso && DIAL[c.iso];
    });

    if (list.length < 2) {
      list = Object.keys(DIAL).map(function (iso) {
        return { iso: iso, name: regionName(iso) };
      });
    }

    return list
      .map(function (c) {
        return {
          iso: c.iso,
          name: c.name || regionName(c.iso),
          dial: '+' + DIAL[c.iso]
        };
      })
      .sort(function (a, b) {
        return a.name.localeCompare(b.name);
      });
  }

  function Combo(root, form) {
    this.root = root;
    this.form = form;
    this.btn = root.querySelector('[data-blv-cs-toggle]');
    this.pop = root.querySelector('[data-blv-cs-pop]');
    this.list = root.querySelector('[data-blv-cs-list]');
    this.search = root.querySelector('[data-blv-cs-search]');
    this.empty = root.querySelector('[data-blv-cs-empty]');
    this.flagEl = root.querySelector('[data-blv-cs-flag]');
    this.codeEl = root.querySelector('[data-blv-cs-code]');

    this.flags = root.dataset.flags !== 'false';
    this.countries = countryList(root);
    this.visible = this.countries.slice();
    this.active = -1;
    this.open = false;
    this.typed = '';
    this.typedAt = 0;

    var want = (root.dataset.default || '').toUpperCase();
    var start = this.countries.filter(function (c) {
      return c.iso === want;
    })[0];
    this.selected = start || this.countries.filter(function (c) {
      return c.iso === 'US';
    })[0] || this.countries[0];

    this.build();
    this.bind();
    this.paint();
  }

  Combo.prototype.build = function () {
    var self = this;
    var frag = document.createDocumentFragment();

    this.countries.forEach(function (c, i) {
      var li = document.createElement('li');
      li.className = 'blv-coming__opt';
      li.setAttribute('role', 'option');
      li.id = self.btn.id + '-opt-' + i;
      li.dataset.iso = c.iso;
      li.dataset.index = String(i);
      li.setAttribute('aria-selected', 'false');

      if (self.flags) {
        var flag = document.createElement('span');
        flag.className = 'blv-coming__opt-flag';
        flag.setAttribute('aria-hidden', 'true');
        flag.textContent = flagEmoji(c.iso);
        li.appendChild(flag);
      }

      var name = document.createElement('span');
      name.className = 'blv-coming__opt-name';
      name.textContent = c.name;
      li.appendChild(name);

      var dial = document.createElement('span');
      dial.className = 'blv-coming__opt-dial';
      dial.textContent = c.dial;
      li.appendChild(dial);

      frag.appendChild(li);
    });

    this.list.appendChild(frag);
    this.options = Array.prototype.slice.call(
      this.list.querySelectorAll('.blv-coming__opt')
    );

    if (this.search && this.countries.length > SEARCH_AFTER) {
      this.search.hidden = false;
    }
  };

  /* Reflects the current selection onto the trigger. */
  Combo.prototype.paint = function () {
    if (!this.selected) return;
    if (this.flagEl) {
      this.flagEl.textContent = this.flags ? flagEmoji(this.selected.iso) : '';
    }
    if (this.codeEl) {
      this.codeEl.textContent = this.flags
        ? this.selected.dial
        : this.selected.iso + ' ' + this.selected.dial;
    }
    var self = this;
    this.options.forEach(function (li) {
      li.setAttribute(
        'aria-selected',
        li.dataset.iso === self.selected.iso ? 'true' : 'false'
      );
    });
  };

  Combo.prototype.dialDigits = function () {
    return this.selected ? digitsOnly(this.selected.dial) : '';
  };

  Combo.prototype.filter = function (term) {
    var q = (term || '').trim().toLowerCase();
    var self = this;
    this.visible = [];

    this.options.forEach(function (li, i) {
      var c = self.countries[i];
      var hit =
        !q ||
        c.name.toLowerCase().indexOf(q) === 0 ||
        c.name.toLowerCase().indexOf(' ' + q) > -1 ||
        c.iso.toLowerCase().indexOf(q) === 0 ||
        c.dial.replace('+', '').indexOf(q.replace('+', '')) === 0;
      li.hidden = !hit;
      if (hit) self.visible.push(i);
    });

    /* Swapping text rather than toggling [hidden] so the status region is
       actually announced — a hidden-to-visible flip with unchanged text is
       unreliable in VoiceOver. */
    if (this.empty) {
      this.empty.textContent = this.visible.length
        ? ''
        : this.empty.dataset.msg || '';
    }

    /* A stale scroll offset from the previous query would leave the new first
       match out of view. */
    this.list.scrollTop = 0;
    this.setActive(this.visible.length ? this.visible[0] : -1, false);
  };

  Combo.prototype.setActive = function (index, scroll) {
    if (this.active > -1 && this.options[this.active]) {
      this.options[this.active].classList.remove('is-active');
    }
    this.active = index;

    var el = index > -1 ? this.options[index] : null;
    if (el) {
      el.classList.add('is-active');
      if (scroll !== false) {
        /* Rows and the <ul> share the popup as offsetParent, so offsetTop is
           measured from the popup while scrollTop is measured from the list.
           Subtracting the list's own offset puts both in one coordinate space
           — without it the search box's height skewed every scroll. */
        var top = el.offsetTop - this.list.offsetTop;
        var bottom = top + el.offsetHeight;
        if (top < this.list.scrollTop) {
          this.list.scrollTop = top;
        } else if (bottom > this.list.scrollTop + this.list.clientHeight) {
          this.list.scrollTop = bottom - this.list.clientHeight;
        }
      }
    }

    var owner = this.search && !this.search.hidden ? this.search : this.list;
    if (el) {
      owner.setAttribute('aria-activedescendant', el.id);
    } else {
      owner.removeAttribute('aria-activedescendant');
    }
  };

  Combo.prototype.step = function (delta) {
    if (!this.visible.length) return;
    var at = this.visible.indexOf(this.active);
    var next = at < 0 ? 0 : at + delta;
    if (next < 0) next = this.visible.length - 1;
    if (next >= this.visible.length) next = 0;
    this.setActive(this.visible[next]);
  };

  /* The popup is position:fixed, so it is placed from the trigger's rect here
     rather than by CSS. It flips above the field when there is not enough room
     below, and the LIST (not the popup) is what gets capped, so the search box
     never scrolls out of reach. */
  Combo.prototype.place = function () {
    var GAP = 8;
    var EDGE = 8;
    var rect = this.btn.getBoundingClientRect();
    var vh = window.innerHeight;
    var vw = window.innerWidth;

    this.list.style.maxHeight = '';
    var chrome = this.pop.offsetHeight - this.list.offsetHeight;

    var below = vh - rect.bottom - GAP - EDGE;
    var above = rect.top - GAP - EDGE;
    var flip = below < 180 && above > below;
    var space = flip ? above : below;

    this.list.style.maxHeight = Math.max(120, space - chrome) + 'px';

    var height = this.pop.offsetHeight;
    var top = flip ? rect.top - GAP - height : rect.bottom + GAP;
    /* -6px lines the popup up with the pill's outer edge (4px field padding
       plus its 1.5px border), then clamped so it never leaves the viewport. */
    var left = rect.left - 6;
    left = Math.max(EDGE, Math.min(left, vw - this.pop.offsetWidth - EDGE));

    this.pop.style.top = Math.max(EDGE, top) + 'px';
    this.pop.style.left = left + 'px';
  };

  Combo.prototype.show = function () {
    if (this.open) return;
    this.open = true;
    this.pop.hidden = false;
    this.btn.setAttribute('aria-expanded', 'true');
    if (this.search && !this.search.hidden) this.search.value = '';
    this.filter('');

    var self = this;
    var at = this.countries.indexOf(this.selected);
    if (at > -1) this.setActive(at);

    this.place();

    /* Fixed positioning does not follow the trigger, so keep it in step while
       the popup is open. */
    this.reflow = function () {
      self.place();
    };
    window.addEventListener('scroll', this.reflow, true);
    window.addEventListener('resize', this.reflow);

    if (this.search && !this.search.hidden) {
      this.search.focus();
    } else {
      this.list.setAttribute('tabindex', '-1');
      this.list.focus();
    }

    /* Deferred so the click that opened the popup does not immediately close
       it on the way back up. */
    this.outside = function (event) {
      if (!self.root.contains(event.target) && !self.pop.contains(event.target)) {
        self.hide(false);
      }
    };
    setTimeout(function () {
      /* Guarded: without it, closing before this fires would attach a listener
         nothing ever removes. */
      if (self.open && self.outside) {
        document.addEventListener('mousedown', self.outside);
      }
    }, 0);
  };

  Combo.prototype.hide = function (refocus) {
    if (!this.open) return;
    this.open = false;
    this.pop.hidden = true;
    this.btn.setAttribute('aria-expanded', 'false');
    if (this.outside) {
      document.removeEventListener('mousedown', this.outside);
      this.outside = null;
    }
    if (this.reflow) {
      window.removeEventListener('scroll', this.reflow, true);
      window.removeEventListener('resize', this.reflow);
      this.reflow = null;
    }
    if (refocus !== false) this.btn.focus();
  };

  Combo.prototype.choose = function (index) {
    if (index < 0 || !this.countries[index]) return;
    this.selected = this.countries[index];
    this.paint();
    this.hide();
    setError(this.form, '');
  };

  /* Type-ahead for the no-search-box case: "ge" jumps to Germany. */
  Combo.prototype.typeAhead = function (ch, now) {
    this.typed = now - this.typedAt > 900 ? ch : this.typed + ch;
    this.typedAt = now;

    var q = this.typed;
    for (var n = 0; n < this.visible.length; n++) {
      var i = this.visible[n];
      if (this.countries[i].name.toLowerCase().indexOf(q) === 0) {
        this.setActive(i);
        return;
      }
    }
  };

  Combo.prototype.bind = function () {
    var self = this;

    this.btn.addEventListener('click', function () {
      if (self.open) self.hide();
      else self.show();
    });

    this.btn.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        self.show();
      }
    });

    this.list.addEventListener('click', function (event) {
      var li = event.target.closest ? event.target.closest('.blv-coming__opt') : null;
      if (li) self.choose(Number(li.dataset.index));
    });

    this.list.addEventListener('mousemove', function (event) {
      var li = event.target.closest ? event.target.closest('.blv-coming__opt') : null;
      if (li) self.setActive(Number(li.dataset.index), false);
    });

    var onKey = function (event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          self.step(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          self.step(-1);
          break;
        case 'Home':
          event.preventDefault();
          if (self.visible.length) self.setActive(self.visible[0]);
          break;
        case 'End':
          event.preventDefault();
          if (self.visible.length) self.setActive(self.visible[self.visible.length - 1]);
          break;
        case 'Enter':
          event.preventDefault();
          self.choose(self.active);
          break;
        case ' ':
          /* Only where there is no search field for Space to type into. */
          if (self.search && !self.search.hidden) break;
          event.preventDefault();
          self.choose(self.active);
          break;
        case 'Escape':
          event.preventDefault();
          self.hide();
          break;
        case 'Tab':
          /* Refocus the trigger BEFORE the default action runs. Hiding the
             popup blurs the focused row to <body>, which made Tab restart
             from the top of the document instead of reaching the phone
             field. No preventDefault — the default is what moves focus on. */
          self.hide();
          break;
        default:
          /* Type-ahead only where there is no search field to type into. */
          if (
            (!self.search || self.search.hidden) &&
            event.key.length === 1 &&
            /\S/.test(event.key) &&
            !event.metaKey &&
            !event.ctrlKey &&
            !event.altKey
          ) {
            self.typeAhead(event.key.toLowerCase(), event.timeStamp);
          }
      }
    };

    this.list.addEventListener('keydown', onKey);

    if (this.search) {
      this.search.addEventListener('keydown', onKey);
      this.search.addEventListener('input', function () {
        self.filter(self.search.value);
        /* Filtering changes the popup's height, so it has to be re-anchored —
           otherwise a flipped-up list detaches from the field. */
        if (self.open) self.place();
      });
    }
  };

  /* ==========================================================================
     SUBMISSION — inline validation, then the confirmation swap
     ========================================================================== */

  /* Deliberately loose. The server is the authority on whether an address
     exists; this only catches what type="email" lets through and Shopify would
     then reject on a round trip — "a@b" being the common one. */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* Shopify answers a successful customer post with a redirect carrying this
     flag. Checked alongside the returned markup, because the response body is
     the page HTML whether the post succeeded or failed. */
  var POSTED = 'customer_posted=true';

  /* A customer form can be answered with a bot challenge instead of a result.
     A fetch cannot complete one, so those hand the post back to the browser. */
  var CHALLENGE = /\/(challenge|checkpoint)/;

  /* Matches what the browser would have sent: {% form %} sets no enctype, so a
     native post is urlencoded, not multipart. */
  function encodeForm(form) {
    var parts = [];

    new FormData(form).forEach(function (value, key) {
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    });

    /* FormData leaves out the submit button, which a native post would have
       sent as the submitter. Added back so what Shopify receives is the same
       request the browser would have made without this script. */
    var btn = form.querySelector('[data-blv-cs-submit][name]');
    if (btn) {
      parts.push(encodeURIComponent(btn.name) + '=' + encodeURIComponent(btn.value || ''));
    }

    return parts.join('&');
  }

  function genericError(form) {
    var box = form.querySelector('[data-blv-cs-clienterror]');
    return (box && box.dataset.msg) || 'Something went wrong. Please try again.';
  }

  function setBusy(form, busy) {
    var btn = form.querySelector('[data-blv-cs-submit]');
    if (!btn) return;

    /* disabled as well as aria-busy: the flag alone still leaves the button
       clickable, and a double post creates the customer twice. */
    btn.disabled = busy;

    if (busy) {
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.removeAttribute('aria-busy');
    }
  }

  /* Swaps the two states the snippet already rendered. Nothing is built here,
     so the confirmation cannot arrive half-styled or before its fonts. */
  function showThanks(form) {
    var signup = form.querySelector('[data-blv-cs-signup]');
    var thanks = form.querySelector('[data-blv-cs-thanks]');
    if (!thanks) return false;

    setError(form, '');
    if (signup) signup.hidden = true;
    thanks.hidden = false;

    /* The control that had focus has just been hidden, which drops focus to
       <body> and restarts tabbing at the top of the document. Moving it onto
       the panel is also what announces the change to a screen reader. */
    thanks.focus();
    return true;
  }

  /* One parse, two answers: whether the server rendered the confirmation, and
     if not what it objected to. Reading the confirmation out of the response is
     a surer success signal than the redirect flag — it is the same markup the
     no-JS path would have shown. */
  function readResponse(html) {
    var out = { posted: false, error: '' };

    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var thanks = doc.querySelector('[data-blv-cs-thanks]');
      var error = doc.querySelector('[data-blv-cs-servererror]');

      out.posted = !!thanks && !thanks.hasAttribute('hidden');
      out.error = error ? error.textContent.trim() : '';
    } catch (e) {
      /* Defaults stand, so the caller falls back to the generic message. */
    }

    return out;
  }

  /* Gives the post back to the browser, carrying the values this script has
     already normalised. The prototype method rather than form.submit(), which
     a field named "submit" would shadow — and it fires no submit event, so
     this cannot re-enter the handler below. */
  function handOff(form) {
    HTMLFormElement.prototype.submit.call(form);
  }

  function submitAjax(form) {
    setBusy(form, true);

    fetch(form.action, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'text/html'
      },
      body: encodeForm(form),
      /* Shopify redirects back to the referring page, so the session cookie
         has to travel with the post. */
      credentials: 'same-origin'
    })
      .then(function (res) {
        return res.text().then(function (html) {
          return { url: res.url || '', html: html };
        });
      })
      .then(function (res) {
        if (CHALLENGE.test(res.url)) {
          handOff(form);
          return;
        }

        var read = readResponse(res.html);

        if (read.posted || res.url.indexOf(POSTED) > -1) {
          /* Defensive: the snippet always renders the panel, but the signup
             has already gone through, so the fallback is to follow the
             redirect and let Liquid render the confirmation. Re-posting
             would be a second write for a form that already succeeded. */
          if (!showThanks(form)) window.location.assign(res.url);
          return;
        }

        setBusy(form, false);
        setError(form, read.error || genericError(form));
      })
      .catch(function () {
        setBusy(form, false);
        setError(form, genericError(form));
      });
  }

  function init(root) {
    var forms = (root || document).querySelectorAll('.blv-coming__form');

    Array.prototype.forEach.call(forms, function (form) {
      if (form.dataset.blvCsBound === 'true') return;

      var comboEl = form.querySelector('[data-blv-cs-combo]');
      var tel = form.querySelector('[data-blv-cs-tel]');
      var emailField = form.querySelector('[data-blv-cs-emailfield]');
      var emailFallback = form.querySelector('[data-blv-cs-email]');
      if (!comboEl || !tel) return;

      /* Set only once the form is genuinely bound. Marking it before the guard
         meant a form that bailed here was recorded as bound, so the
         shopify:section:load re-init skipped it for good. */
      form.dataset.blvCsBound = 'true';

      var combo = new Combo(comboEl, form);

      var clear = function () {
        setError(form, '');
      };

      /* Composes the E.164 number and, when the email field is off, the
         placeholder address Shopify needs to create the record at all.
         Returns null when the number is not usable yet. */
      var compose = function () {
        var raw = (tel.value || '').trim();
        var intl = raw.charAt(0) === '+' || raw.indexOf('00') === 0;
        var dialDigits = combo.dialDigits();
        var iso = combo.selected && combo.selected.iso;
        var national = intl
          ? digitsOnly(raw).replace(/^00/, '')
          : nationalDigits(raw, dialDigits, iso);
        var full = intl ? national : dialDigits + national;

        if (!dialDigits || national.length < MIN_DIGITS || full.length > MAX_DIGITS) return null;
        return full;
      };

      /* Written as the number is typed, not only on submit. If anything stops
         the submit handler completing, the field Shopify validates is already
         populated, so the post cannot be answered with "Email can't be blank."
         — an error about an input this form never showed. */
      var carry = function () {
        if (!emailFallback) return;
        var full = compose();
        if (full) emailFallback.value = full + '@' + EMAIL_DOMAIN;
      };

      tel.addEventListener('input', clear);
      tel.addEventListener('input', carry);
      if (emailField) emailField.addEventListener('input', clear);

      form.addEventListener('submit', function (event) {
        var canAjax = !!(window.fetch && window.FormData && window.DOMParser);

        try {
          if (emailField) {
            var address = (emailField.value || '').trim();

            if (!EMAIL_RE.test(address)) {
              event.preventDefault();
              setError(
                form,
                emailField.dataset.blvCsInvalid || 'Please enter a valid email address.',
                emailField
              );
              emailField.focus();
              return;
            }

            /* Trimmed value posted, so a trailing space pasted in with the
               address does not reach the customer record. */
            emailField.value = address;
          }

          var raw = (tel.value || '').trim();

          /* With the email field off the number is the only identifier Shopify
             has to work from, so it is required whatever the setting says. */
          var telRequired = tel.required || !emailField;

          if (telRequired || raw !== '') {
            /* A leading + or 00 is an explicit country code — trust what was typed
               instead of prefixing the selection on top of it, which turned a UK
               number pasted under a US selection into +1447911123456. */
            var intl = raw.charAt(0) === '+' || raw.indexOf('00') === 0;
            var dialDigits = combo.dialDigits();
            var iso = combo.selected && combo.selected.iso;
            var national = intl
              ? digitsOnly(raw).replace(/^00/, '')
              : nationalDigits(raw, dialDigits, iso);
            var full = intl ? national : dialDigits + national;

            if (!dialDigits || national.length < MIN_DIGITS || full.length > MAX_DIGITS) {
              event.preventDefault();
              setError(form, tel.dataset.blvCsInvalid || 'Please enter a valid phone number.', tel);
              tel.focus();
              return;
            }

            tel.value = '+' + full;

            /* Rewritten every submit, not just when empty, so a corrected number
               does not leave the previous number's placeholder behind. Only in
               play while the email field is off — the two are mutually
               exclusive, or two fields would post as contact[email]. */
            if (emailFallback) emailFallback.value = full + '@' + EMAIL_DOMAIN;
          }

          /* Nothing can carry the address Shopify builds the record from, so the
             post would come back rejected on a field that is not on screen.
             Stop here instead of spending a round trip to find that out. */
          if (!emailField && !emailFallback) {
            event.preventDefault();
            setError(form, genericError(form), tel);
            return;
          }

          setError(form, '');
        } catch (e) {
          /* An exception thrown in a submit listener does NOT cancel the
             submit — the browser goes ahead and posts the form as it stands.
             With the email field off that means posting an unfilled hidden
             input, which Shopify answers with "Email can't be blank.": an
             error naming a field the visitor never saw, after a full page
             reload. Catching it keeps the failure on this page, in the store's
             own words, with the number still in the field to retry. */
          event.preventDefault();
          setBusy(form, false);
          setError(form, genericError(form), tel);
          return;
        }

        /* Below here is the confirmation-without-reload path. Where fetch,
           FormData or DOMParser is missing, the native post runs instead and
           the snippet renders the confirmation server-side — same outcome, one
           page load heavier. */
        if (!canAjax) return;

        event.preventDefault();
        submitAjax(form);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Re-bind when the Customizer swaps the section in. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
